<!-- special-period-form.component.html -->
<div
  class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
>
  <div
    class="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden"
  >
    <!-- Header -->
    <div class="px-6 pt-6 pb-4 border-b border-gray-100 dark:border-slate-800">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
        {{
          isEditMode ? "✏️ Modifier la période" : "➕ Nouvelle période spéciale"
        }}
      </h2>
      <p class="text-sm text-gray-500 dark:text-slate-400 mt-1">
        Les périodes spéciales s'affichent sur votre calendrier et ajustent les
        recommandations.
      </p>
    </div>

    <!-- Form -->
    <div class="px-6 py-5 space-y-4">
      <!-- Type -->
      <div>
        <label
          class="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
        >
          Type de période
        </label>
        <div class="grid grid-cols-2 gap-2 sm:grid-cols-3">
          @for (t of periodTypes; track t) {
            <button
              type="button"
              (click)="type = t; onTypeChange()"
              class="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-all"
              [class]="
                type === t
                  ? 'bg-purple-100 dark:bg-purple-900/40 border-purple-400 dark:border-purple-500 text-purple-800 dark:text-purple-200 font-medium'
                  : 'bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:border-purple-300'
              "
            >
              <span>{{ PERIOD_TYPE_META[t].icon }}</span>
              <span>{{ PERIOD_TYPE_META[t].label }}</span>
            </button>
          }
        </div>
      </div>

      <!-- Libellé -->
      <div>
        <label
          class="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
        >
          Libellé
          @if (isCustomType) {
            <span class="text-red-500">*</span>
          }
        </label>
        @if (isCustomType) {
          <input
            type="text"
            [(ngModel)]="label"
            placeholder="Ex: Vacances d'été, Marché de Noël..."
            maxlength="100"
            class="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        } @else {
          <div
            class="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60"
          >
            <span class="text-lg leading-none">{{
              PERIOD_TYPE_META[type].icon
            }}</span>
            <span
              class="text-sm font-medium text-gray-700 dark:text-slate-300"
              >{{ label }}</span
            >
            <span
              class="ml-auto text-xs text-gray-400 dark:text-slate-500 italic"
              >Auto</span
            >
          </div>
        }
      </div>

      <!-- Dates -->
      <div class="grid grid-cols-2 gap-3">
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
          >
            Début <span class="text-red-500">*</span>
          </label>
          <input
            type="date"
            [(ngModel)]="startDate"
            class="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
          >
            Fin <span class="text-red-500">*</span>
          </label>
          <input
            type="date"
            [(ngModel)]="endDate"
            class="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>

      <!-- Couleur + Notes (collapsible optionnel) -->
      <div class="grid grid-cols-2 gap-3">
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
          >
            Couleur
          </label>
          <div class="flex items-center gap-2">
            <input
              type="color"
              [(ngModel)]="color"
              class="h-9 w-12 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 cursor-pointer"
            />
            <span class="text-xs text-gray-500 dark:text-slate-500">{{
              color
            }}</span>
          </div>
        </div>
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
          >
            Notes
          </label>
          <textarea
            [(ngModel)]="notes"
            rows="2"
            maxlength="500"
            placeholder="Optionnel..."
            class="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
          ></textarea>
        </div>
      </div>

      <!-- Erreur -->
      @if (error()) {
        <p
          class="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2"
        >
          ⚠️ {{ error() }}
        </p>
      }
    </div>

    <!-- Footer actions -->
    <div class="px-6 pb-6 flex justify-end gap-3">
      <button
        type="button"
        (click)="cancel()"
        class="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
      >
        Annuler
      </button>
      <button
        type="button"
        (click)="submit()"
        [disabled]="saving()"
        class="px-4 py-2 rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        @if (saving()) {
          Enregistrement...
        } @else {
          {{ isEditMode ? "Modifier" : "Créer" }}
        }
      </button>
    </div>
  </div>
</div>
